import Panel from "components/panels/panel";
import React from "react";
import { getCityStateCountry } from "shared/utils/addressUtils";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import UnlinkModal from "./UnlinkModal";
import classNames from "classnames";
import NewCompanyModal from "./NewCompanyModal";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import { PhosphorLinkBreakButton } from "components/Atoms/PhosphorIconButton";

interface Props {
  config: server.dto.LinkedCompanyPanelConfig
}

const WorksAt = (props: Props) => {
  const maxPerPage = 10;

  const [unlinkingCompany, setUnlinkingCompany] = React.useState<server.dto.CompanyWithWorkRelation>(undefined);
  const [displayNewCompanyModal, setDisplayNewCompanyModal] = React.useState<boolean>(false);

  const [instruction, setInstruction] = React.useState<server.dto.ListCompanyByContactInstruction>({
    contactId: props.config.itemId,
    page: 1,
    perPage: maxPerPage
  });

  const worksAtApi = useCustomQuery({
    queryKey: ["worksAt", instruction],
    queryFn: () => window.CoreApi.CompanyToContact.ListCompanyByContact(instruction).then(r => r.data),
    onSuccess: res => {
      pagination.setPagination(res.pagination);
    }
  });

  const pagination = useManualPagination(1, maxPerPage, (pagination) => {
    setInstruction(prevInstr => ({
      ...prevInstr,
      page: pagination.page
    }));
  });

  const { refToScrollTo } = useShortcutPanel({
    type: "worksAt",
    label: props.config.headerLabel,
    totalItems: pagination.total,
    onAddClicked: props.config.canAdd ? () => setDisplayNewCompanyModal(true) : undefined
  });

  const onUnlinkModalClosed = (anythingChanged: boolean) => {
    setUnlinkingCompany(undefined);
    if (anythingChanged) {
      worksAtApi.refetch();
    }
  };

  const onNewCompanyLinked = async (companyId: number) => {
    setDisplayNewCompanyModal(false);
    const res = await window.CoreApi.CompanyToContact.SetLink({
      companyId,
      contactId: props.config.itemId,
      defaultSelect: false,
      isActive: true
    });

    if (res.data.hasErrors) {
      window.displayGenericModal("Error", res.data.errors[0].message, "tiny", "red");
    }

    worksAtApi.refetch();
  };

  const transparentIfInactive = (companyRelation: server.dto.CompanyWithWorkRelation, cssClasses?: string): string => {
    return classNames(cssClasses, {
      s1_transparent: !companyRelation.isActive
    });
  };

  const canModify = (companyRelation: server.dto.CompanyWithWorkRelation) => {
    return companyRelation.permissions.canModify;
  };

  return <>
    {unlinkingCompany && <UnlinkModal
      contactId={props.config.itemId}
      companyWithWorkRelation={unlinkingCompany}
      onClose={onUnlinkModalClosed}
    />}

    {displayNewCompanyModal && <NewCompanyModal
      onClose={() => setDisplayNewCompanyModal(false)}
      onCompanyLinked={onNewCompanyLinked}
      contactId={props.config.itemId}
      excludeKeys={worksAtApi.data?.data.map(c => c.company.companyId)}
    />}

    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      pagination={pagination.Component}
      hasData={worksAtApi.data?.data.length > 0}
      button={props.config.canAdd && <ButtonSecondary onClick={() => setDisplayNewCompanyModal(true)} label={RESX.GeneralLabels.resxNew} />}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {worksAtApi.data?.data.map(c => (
            <div key={c.company.companyId}>
              <div className={transparentIfInactive(c, "grow1")}>
                <div>
                  <CompanySidePanel type="company"
                    companyId={c.company.companyId}
                    trigger={<a className="table-main-link">{c.company.name}</a>}
                  />
                </div>
                <div className="margin-top-small s1_gray">
                  {getCityStateCountry(c.company.address)}
                </div>
              </div>

              {canModify(c) && <PhosphorLinkBreakButton
                layout="in-square"
                onClick={() => setUnlinkingCompany(c)} />}

            </div>
          ))}
        </div>
      </div>
    </Panel>
  </>;
};

export default WorksAt;